import { format } from 'date-fns';


// Get job source badge color
export const getSourceBadgeColor = (source) => {
  switch (source) {
    case 'rss':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'practice':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

// Get job type badge colors
export const getJobTypeBadgeColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'full-time':
    case 'full time':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'part-time':
    case 'part time':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'locum':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'contract':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'temporary':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'permanent':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

// Format currency for UK
export const formatCurrency = (amount) => {
  if (!amount || amount === 0) return 'Negotiable';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount);
};

// Calculate distance between coordinates in miles (UK standard)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Radius of the Earth in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in miles
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

// SIMPLIFIED: Only color mapping, no classification logic

/**
 * Get color for employment type - uses exact string matching only
 * No more inference or normalization logic here
 */
export const getJobTypeColor = (employmentType) => {
  // Handle null/undefined/empty
  if (!employmentType) {
    return '#6b7280'; // Gray for missing types
  }

  // Exact string matching against standardized values
  switch (employmentType) {
    case 'Full-time':
      return '#059669'; // Green
    case 'Part-time':
      return '#0891b2'; // Cyan  
    case 'NHS':
      return '#dc2626'; // Red
    case 'Contract':
      return '#ea580c'; // Orange
    case 'Locum':
      return '#7c3aed'; // Purple
    case 'Permanent':
      return '#059669'; // Green (same as full-time)
    case 'Temporary':
      return '#7c3aed'; // Purple (same as locum)
    default:
      return '#6b7280'; // Gray for unrecognized types
  }
};

export const getRoleColor = (role) => {
  switch (role) {
    case 'Hygienist/Therapist':
      return '#3b82f6'; // Blue
    case 'Locum Dentist':
      return '#10b981'; // Emerald
    case 'Dental Nurse':
      return '#f59e0b'; // Amber
    default:
      return '#6b7280'; // Gray
  }
};

// Add utility for better distance formatting
export const formatDistance = (distance) => {
  if (!distance || typeof distance !== 'number') {
    return '';
  }
  
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m away`;
  }
  
  // Round to 2 decimal places maximum, but remove trailing zeros
  const rounded = Math.round(distance * 100) / 100;
  return `${rounded} miles away`;
};

export const getJobTypeDisplayName = (type) => {
  // Just return the type as-is since they're now standardized
  return type || 'Unknown';
};

// FIXED: Updated to prioritize postedAt for both RSS and practice jobs
export const getJobPostedDate = (job) => {
  // Priority order: postedAt, createdAt, scrapedAt, lastUpdated
  if (job.postedAt) {
    if (job.postedAt.seconds) {
      return new Date(job.postedAt.seconds * 1000);
    }
    if (job.postedAt instanceof Date) {
      return job.postedAt;
    }
    if (typeof job.postedAt === 'string') {
      return new Date(job.postedAt);
    }
  }
  
  // Fallback to createdAt for practice jobs
  if (job.createdAt) {
    if (job.createdAt.seconds) {
      return new Date(job.createdAt.seconds * 1000);
    }
    if (job.createdAt instanceof Date) {
      return job.createdAt;
    }
    if (typeof job.createdAt === 'string') {
      return new Date(job.createdAt);
    }
  }
  
  // Last resort fallbacks
  if (job.scrapedAt && job.scrapedAt.seconds) {
    return new Date(job.scrapedAt.seconds * 1000);
  }
  
  if (job.lastUpdated && job.lastUpdated.seconds) {
    return new Date(job.lastUpdated.seconds * 1000);
  }
  
  // Final fallback
  return new Date();
};

// Format date for job listings
export const formatJobDate = (date) => {
  if (!date) return 'Recently';
  
  try {
    let dateObj = null;
    
    // Handle Firestore timestamp objects
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    }
    
    if (!dateObj || isNaN(dateObj.getTime())) {
      console.warn('Invalid date provided to formatJobDate:', date);
      return 'Recently';
    }
    
    const now = new Date();
    const diffTime = Math.abs(now - dateObj);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    /*console.log('formatJobDate - Details:', {
      input: date,
      parsed: dateObj,
      now: now,
      diffTime: diffTime,
      diffDays: diffDays,
      nowTimestamp: now.getTime(),
      dateTimestamp: dateObj.getTime()
    });*/
    
    if (diffDays === 0) {
      return 'today';
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return years === 1 ? '1 year ago' : `${years} years ago`;
    }
  } catch (error) {
    console.error('Error formatting date:', error, 'Date input:', date);
    return 'Recently';
  }
};

export const sortJobs = (jobs, sortBy) => {
  // Add check to see if practice jobs are in the array
  const practiceJobs = jobs.filter(job => job.source === 'practice');
  const rssJobs = jobs.filter(job => job.source === 'rss');
  
  console.log(`sortJobs called with ${jobs.length} total jobs:`, {
    sortBy,
    practiceJobs: practiceJobs.length,
    rssJobs: rssJobs.length,
    practiceJobIds: practiceJobs.map(j => j.id),
    rssJobIds: rssJobs.map(j => j.id)
  });
  
  return [...jobs].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        const dateA = getJobPostedDate(a);
        const dateB = getJobPostedDate(b);
        
        // Handle null/undefined dates
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1; // Push jobs without dates to end
        if (!dateB) return -1; // Push jobs without dates to end
        
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        
        // Handle invalid dates
        if (isNaN(timeA) && isNaN(timeB)) return 0;
        if (isNaN(timeA)) return 1;
        if (isNaN(timeB)) return -1;
        
        return timeB - timeA; // Newest first (higher timestamp first)
        
      case 'oldest':
        const oldDateA = getJobPostedDate(a);
        const oldDateB = getJobPostedDate(b);
        
        // Handle null/undefined dates
        if (!oldDateA && !oldDateB) return 0;
        if (!oldDateA) return 1;
        if (!oldDateB) return -1;
        
        const oldTimeA = new Date(oldDateA).getTime();
        const oldTimeB = new Date(oldDateB).getTime();
        
        // Handle invalid dates
        if (isNaN(oldTimeA) && isNaN(oldTimeB)) return 0;
        if (isNaN(oldTimeA)) return 1;
        if (isNaN(oldTimeB)) return -1;
        
        return oldTimeA - oldTimeB; // Oldest first (lower timestamp first)
        
      case 'salary-high':
        const salaryA = a.salary || 0;
        const salaryB = b.salary || 0;
        return salaryB - salaryA;
        
      case 'salary-low':
        const salaryLowA = a.salary || 0;
        const salaryLowB = b.salary || 0;
        return salaryLowA - salaryLowB;
        
      case 'distance':
        const distanceA = a.distance || 999;
        const distanceB = b.distance || 999;
        return distanceA - distanceB;
        
      default:
        return 0;
    }
  });
};
